// Join page with room code form

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldError, FieldGroup } from '@/components/ui/field';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Separator } from '@/components/ui/separator';
import SEO from '../seo/SEO';
import { verifyRoomExists } from '../transport/api/publicApi';
import { analyticsService } from '../services/analyticsService';
import { createLogger } from '@airhead-games/shared/utilities';
import { roomCodePattern } from '@airhead-games/shared/utilities/idPatterns';
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import {
  InputValidationError,
  useValidatedInput,
} from '../hooks/useValidatedInput';

const logger = createLogger('Join');

const Join: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [prefilledCode, setPrefilledCode] = useState<string>('');

  const roomCode = useValidatedInput({
    rules: ['onlyNumbers', 'minLength:6', 'maxLength:6'],
    emptyMessage: 'Please enter a room code.',
    onSubmit: async (normalizedRoomCode) => {
      logger.info('Attempting to join room', normalizedRoomCode, '...');

      try {
        const response = await verifyRoomExists(normalizedRoomCode);
        if (!response) {
          throw new InputValidationError(
            'Invalid room code. Please try again.'
          );
        }

        logger.info('Room exists, navigating to player screen');

        const joinMethod = prefilledCode ? 'url' : 'form';
        analyticsService.trackRoomJoined({
          room_code: normalizedRoomCode,
          join_method: joinMethod,
        });

        navigate(`/room/${normalizedRoomCode}/player`);
      } catch (error) {
        if (error instanceof InputValidationError) throw error;
        logger.error('Room verification failed:', error);
        throw new InputValidationError('Invalid room code. Please try again.');
      }
    },
  });
  const setRoomCodeValue = roomCode.setValue;

  // Extract room code from query parameter
  useEffect(() => {
    const searchString = window.location.search;
    // Extract the first parameter value (everything after ?)
    const match = searchString.match(/\?([^&]+)/);
    if (match && match[1]) {
      const potentialCode = match[1];
      // Only pre-fill if it matches the room code pattern (6 digits)
      if (roomCodePattern.test(potentialCode)) {
        setPrefilledCode(potentialCode);
        setRoomCodeValue(potentialCode);
        logger.debug('Prefilled room code from URL:', potentialCode);
      } else {
        logger.warn('Invalid room code format in URL:', potentialCode);
      }
    }
  }, [searchParams, setRoomCodeValue]);

  return (
    <div className="min-h-svh bg-background text-foreground">
      <SEO />
      <main className="mx-auto flex min-h-svh w-full max-w-md items-center px-6 py-10">
        <Card className="w-full text-base">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Enter Room Code to Join</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <form
              className="w-full"
              onSubmit={(event) => {
                event.preventDefault();
                void roomCode.submit();
              }}
            >
              <FieldGroup>
                <Field data-invalid={!!roomCode.error}>
                  <InputOTP
                    id="join-room-code"
                    maxLength={6}
                    pattern={REGEXP_ONLY_DIGITS}
                    value={roomCode.value}
                    onChange={(value) => {
                      roomCode.setValue(value);
                      if (roomCode.error) roomCode.setError(null);
                      if (value.length === 6 && !roomCode.isSubmitting) {
                        void roomCode.submit(value);
                      }
                    }}
                    aria-invalid={!!roomCode.error}
                    disabled={roomCode.isSubmitting}
                  >
                    <InputOTPGroup className="w-full *:data-[slot=input-otp-slot]:h-12 *:data-[slot=input-otp-slot]:flex-1 *:data-[slot=input-otp-slot]:text-xl">
                      <InputOTPSlot index={0} aria-invalid={!!roomCode.error} />
                      <InputOTPSlot index={1} aria-invalid={!!roomCode.error} />
                      <InputOTPSlot index={2} aria-invalid={!!roomCode.error} />
                      <InputOTPSlot index={3} aria-invalid={!!roomCode.error} />
                      <InputOTPSlot index={4} aria-invalid={!!roomCode.error} />
                      <InputOTPSlot index={5} aria-invalid={!!roomCode.error} />
                    </InputOTPGroup>
                  </InputOTP>
                  {roomCode.error ? (
                    <FieldError>{roomCode.error}</FieldError>
                  ) : null}
                </Field>
                <Button
                  type="submit"
                  disabled={roomCode.isSubmitting}
                  className="w-full cursor-pointer"
                  size="xl"
                >
                  {roomCode.isSubmitting ? 'Joining...' : 'Play'}
                </Button>
              </FieldGroup>
            </form>
            <Separator />
            <p className="text-center text-sm text-muted-foreground">
              You agree to our{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer text-primary underline-offset-4 hover:underline"
              >
                Terms
              </a>{' '}
              by using our service.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Join;
